import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectAddresses,
  selectCurrentUser,
  selectDefaultAddress,
  selectUserInitial,
} from '../selectors';
import { logoutUser } from '../userSlice';
import { useGetMyOrdersQuery, transformConsumerListEntry } from '@/features/checkout/ordersApi';
import { useGetLoyaltyBalanceQuery } from '@/features/loyalty/loyaltyApi';
import Price from '@/components/ui/Price';

export default function AccountHomePage() {
  const { t } = useTranslation();
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const user = useAppSelector(selectCurrentUser);
  const initial = useAppSelector(selectUserInitial);
  const addresses = useAppSelector(selectAddresses);
  const defaultAddr = useAppSelector(selectDefaultAddress);
  const { data: ordersPage } = useGetMyOrdersQuery(
    { restaurantUuid, page: 1, pageSize: 1 },
    { skip: !restaurantUuid },
  );
  const { data: loyalty } = useGetLoyaltyBalanceQuery(restaurantUuid, { skip: !restaurantUuid });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (!user) return null;
  const orderCount = ordersPage?.count ?? 0;
  const recent = ordersPage?.results[0] ? transformConsumerListEntry(ordersPage.results[0]) : undefined;
  const base = `/${restaurantUuid}/account`;

  return (
    <div className="max-w-[800px] mx-auto px-4 py-8 space-y-6">
      <h1 className="font-display text-3xl text-ink">{t('account.dashboard.title')}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loyalty?.enabled && (
          <Link to={`${base}/loyalty`} className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn hover:-translate-y-[2px] hover:-translate-x-[2px] transition-transform">
            <h2 className="font-display text-xl text-ink mb-2">🎁 {t('loyalty.dashboardCardTitle')}</h2>
            <p className="font-display text-3xl text-[var(--on-primary)] bg-mint inline-block px-3 py-1 rounded-btn-sm">
              {t('loyalty.pointsUnit', { n: loyalty.balance })}
            </p>
            <p className="mt-3 text-sm underline">{t('loyalty.viewRewards')}</p>
          </Link>
        )}

        <Link to={`${base}/profile`} className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn hover:-translate-y-[2px] hover:-translate-x-[2px] transition-transform">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex w-10 h-10 items-center justify-center bg-ink text-paper font-display rounded-full">{initial}</span>
            <h2 className="font-display text-xl text-ink">👤 {t('account.dashboard.cards.profile')}</h2>
          </div>
          <p className="text-ink">{user.name}</p>
          <p className="text-sm text-ink/70">{user.email}</p>
          <p className="mt-3 text-sm underline">{t('accountHome.edit')}</p>
        </Link>

        <Link to={`${base}/orders`} className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn hover:-translate-y-[2px] hover:-translate-x-[2px] transition-transform">
          <h2 className="font-display text-xl text-ink mb-2">📦 {t('account.dashboard.cards.orders')}</h2>
          <p className="font-display text-3xl text-ink">{orderCount}</p>
          {recent ? (
            <p className="text-sm text-ink/70 mt-1">
              {t('accountHome.recent', { n: recent.lineCount })}<Price amountCents={recent.total} /> · {labelStatus(recent.status, t)}
            </p>
          ) : (
            <p className="text-sm text-ink/70 mt-1">{t('accountHome.noOrders')}</p>
          )}
          <p className="mt-3 text-sm underline">{t('accountHome.viewAll')}</p>
        </Link>

        <Link to={`${base}/addresses`} className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn hover:-translate-y-[2px] hover:-translate-x-[2px] transition-transform">
          <h2 className="font-display text-xl text-ink mb-2">📍 {t('account.dashboard.cards.addresses')}</h2>
          {defaultAddr ? (
            <p className="text-ink">{defaultAddr.label} · {defaultAddr.street}</p>
          ) : (
            <p className="text-ink/70">{t('accountHome.noAddress')}</p>
          )}
          {addresses.length > 1 && (
            <p className="text-sm text-ink/70">{t('accountHome.moreAddresses', { n: addresses.length - 1 })}</p>
          )}
          <p className="mt-3 text-sm underline">{t('accountHome.manage')}</p>
        </Link>

        <LogoutCard onConfirm={() => { dispatch(logoutUser()); navigate(`/${restaurantUuid}`); }} />
      </div>
    </div>
  );
}

function LogoutCard({ onConfirm }: { onConfirm: () => void }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const startConfirm = () => {
    setConfirming(true);
    setTimeout(() => setConfirming(false), 1500);
  };

  return (
    <div className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn">
      <h2 className="font-display text-xl text-ink mb-2">🚪 {t('account.dashboard.cards.logout')}</h2>
      {confirming ? (
        <div className="flex gap-2">
          <button type="button" onClick={onConfirm}
            className="flex-1 py-2 bg-ink text-paper font-display rounded-btn-sm">{t('account.dashboard.actions.confirm')}</button>
          <button type="button" onClick={() => setConfirming(false)}
            className="flex-1 py-2 bg-paper border-2 border-ink font-display rounded-btn-sm">{t('common.cancel')}</button>
        </div>
      ) : (
        <button type="button" onClick={startConfirm}
          className="w-full py-2 bg-paper border-2 border-ink font-display rounded-btn-sm hover:-translate-y-[1px] hover:-translate-x-[1px] transition-transform">
          {t('account.dashboard.cards.logout')}
        </button>
      )}
    </div>
  );
}

function labelStatus(s: 'completed' | 'in-delivery' | 'cancelled', t: TFunction) {
  if (s === 'completed') return t('accountHome.statusCompleted');
  if (s === 'in-delivery') return t('accountHome.statusInDelivery');
  return t('accountHome.statusCancelled');
}
